import { Alert, flash, Label, Modal, SubmitButton } from "@xola/ui-kit";
import { AxiosError } from "axios";
import {
    getExperiencePurchaseItems,
    isEquipmentItem,
    isOtherSellerPurchase,
    now,
    PurchaseActionOption,
    PurchasePriceStrategy,
    SummarySkeleton,
} from "common";
import { findLast, groupBy, isEmpty, isEqual, lowerCase, minBy, omit } from "lodash";
import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries } from "react-query";
import { Except } from "type-fest";
import {
    Event,
    Experience,
    ExperiencePurchaseItem,
    Guest,
    LineItem,
    LineItemType,
    Package,
    PartnerPackage,
    Permission,
    Purchase,
    PurchaseItem,
    PurchaseItemPrivacy,
    PurchaseRuleFilterError,
    PurchaseRuleFilterErrorCode,
    Timeslot,
} from "types";
import { NotifyCustomerCheckbox } from "../../Common/NotifyCustomerCheckbox";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { RefundOption, RefundSelectOption } from "../../Dashboard/RefundOption";
import { updateTimeslotCache } from "../../Event/EditEventCapacityModal";
import { Loader } from "../../Loader";
import { CouponValidationModal } from "../CouponValidationModal";
import { getNextPurchaseModificationActions } from "../helpers/getNextPurchaseModificationActions";
import { PaymentBreakdown, PriceStrategy } from "../PaymentBreakdown";
import { PurchaseItemSelect } from "../PurchaseItemSelect";
import { PurchaseItemSummaryTile } from "../PurchaseItemSummaryTile";
import { GuestPayload } from "./AddGuest";
import { DemographicList } from "./DemographicList";
import { RefundOptions } from "./RefundOptions";
import { api } from "@/api/api";
import { findValue } from "@/api/interceptors";
import { ValidationModalState } from "@/components/PurchasesMenu/ApplySubmitCodeHelper";
import { useExperience } from "@/hooks/resources/useProduct";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useInvalidate } from "@/hooks/useInvalidation";
import { useResource } from "@/hooks/useResource";
import { useModal } from "@/hooks/useVisibility";
import { getExperienceFromQueryCache, getMaxAttendees, isAlwaysPrivateExperience } from "@/models/Experience";
import { sortGuestsByDisplayOrder } from "@/models/Guest";
import { isSharedDemographicsPackage } from "@/models/Package";
import { getPurchaseBalanceForRefund, isPackageItem, isPartnerPackageItem } from "@/models/Purchase";
import {
    getCouponLineItems,
    getFirstAvailableExperiencePurchaseItem,
    isAllowedPrivacy,
    isPriceConfigurationUpdated,
} from "@/models/PurchaseItem";
import { shouldRetainPriceByDefault } from "@/models/Seller";
import { getSeller } from "@/utils/getSeller";
import { updateCache } from "@/utils/updateCache";

interface UpdatePurchasePayloadItem {
    id: ID;
    guests: Array<Partial<GuestPayload>>;
    _priceStrategy?: PriceStrategy;
    _partialRefund?: number;
}

interface UpdatePurchasePayload {
    items: UpdatePurchasePayloadItem[];
}

interface UpdatePurchaseArguments extends UpdatePurchasePayload {
    dryRun?: boolean;
    _emailOverrides?: { sendEmailToTraveler: boolean };
}

interface PurchaseChangeQuantityModalProps {
    purchase: Purchase;
    item: ExperiencePurchaseItem;
    isOpen: boolean;
    onClose: (
        modalOption: PurchaseActionOption | null,
        refundOption: RefundOption | null,
        refundAmount: number,
        newPurchase?: Purchase,
        refundedItem?: PurchaseItem,
    ) => void;
    onContinue?: () => void;
    guests?: Guest[];
    attendee?: { removedAttendee?: ID; isChangeDemographic?: boolean };
    shouldInvalidate?: boolean;
}

export const PurchaseChangeQuantityModal = ({
    purchase,
    item,
    isOpen,
    onClose,
    onContinue,
    guests,
    attendee,
    shouldInvalidate,
}: PurchaseChangeQuantityModalProps) => {
    return (
        // Passing as onClose={onClose} will pass JS event as 1st param which is not expected,
        // and will cause the JS event to be set as `modalOption`.
        <Modal size="huge" isOpen={isOpen} onClose={() => onClose(null, null, 0)}>
            <ModalContent
                purchase={purchase}
                item={item}
                guests={guests}
                attendee={attendee}
                shouldInvalidate={shouldInvalidate}
                onContinue={onContinue}
                onClose={onClose}
            />
        </Modal>
    );
};

const ModalContent = ({
    purchase: initialPurchase,
    item: passedItem,
    onClose,
    onContinue,
    guests: initialGuests,
    attendee,
    shouldInvalidate = true,
}: Except<PurchaseChangeQuantityModalProps, "isOpen">) => {
    const initialItem = getFirstAvailableExperiencePurchaseItem(initialPurchase, passedItem);
    const invalidate = useInvalidate();
    const { isGranted } = useAuthorization();
    const [localAttendee, setLocalAttendee] = useState(attendee);
    const [breakdown, setBreakdown] = useState<Purchase | null>();
    const [purchase, setPurchase] = useState(initialPurchase);
    const [refundOption, setRefundOption] = useState<RefundSelectOption | null>(null);
    const purchaseBalance = getPurchaseBalanceForRefund(purchase, breakdown, refundOption);
    const [initialRefundAmount, setInitialRefundAmount] = useState(purchaseBalance);
    const [refundAmount, setRefundAmount] = useState(purchaseBalance < 0 ? purchaseBalance : 0);
    const [defaultItem, setDefaultItem] = useState(initialItem);
    const [item, setItem] = useState(initialItem);
    const [equipmentLineItems, setEquipmentLineItems] = useState<LineItem[]>(
        item.lineItems?.filter((li) => li.type === LineItemType.EQUIPMENT) ?? [],
    );
    const hasLineItems = item.lineItems?.length > 0;
    const [retainPrice, setRetainPrice] = useState(!shouldRetainPriceByDefault());
    const [isRetainPriceLoading, setIsRetainPriceLoading] = useState(false);
    const [showRetainPriceToggle, setShowRetainPriceToggle] = useState(false);
    const [notifyCustomer, setNotifyCustomer] = useState(true);
    const isPackage = isPackageItem(item);
    const parentProductId = isPackage && item?.parentProduct?.id;
    const { data: pkg } = useResource<Package | PartnerPackage>(["products", parentProductId], {
        isEnabled: !!parentProductId,
    });

    const { data: experience, isLoading: isExperienceLoading } = useExperience(item.product.id);

    const { data: timeslot, isLoading } = useTimeslot(item, purchase, pkg);

    const itemIds = useMemo(
        () => (isSharedDemographicsPackage(pkg) ? purchase.items.map((purchaseItem) => purchaseItem.id) : [item.id]),
        [pkg, purchase, item.id],
    );

    const [isDryRunPurchase, setIsDryRunPurchase] = useState(true);

    const [validationModalState, setValidationModalState] = useState<ValidationModalState>({
        isOpen: false,
        validationLevel: "",
        message: "",
    });
    const shouldShowPrivacyWarning =
        experience?.allowedPrivacies &&
        isAllowedPrivacy(item.privacy) &&
        !experience.allowedPrivacies.includes(item.privacy);
    const supportedPrivacy = experience?.allowedPrivacies?.[0];

    const couponRestrictionModal = useModal(false);

    const guests = getGuestsWithoutCheckInCount(
        initialGuests
            ? item.guests.filter((guest) => initialGuests?.some((initialGuest) => initialGuest.id === guest.id))
            : item.guests,
    );
    const isDifferentSellerPurchase = isOtherSellerPurchase(purchase, getSeller().id);

    const { data: event } = useResource<Event>(["events", item.event?.id], null, {
        enabled: !isDifferentSellerPurchase,
    });

    const [isPaymentBreakdownLoading, setIsPaymentBreakdownLoading] = useState(false);

    useEffect(() => {
        handleRefundAmountChange();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [breakdown]);

    const handleRefundAmountChange = () => {
        if (!isPurchaseChanged(initialItem, updatedGuests, isEquipmentItem(item) ? equipmentLineItems : undefined)) {
            setRefundOption(null);
            setRefundAmount(0);
            return;
        }

        if (purchaseBalance > 0) {
            setRefundAmount(purchaseBalance);
            setRefundOption(null);
            return;
        }

        if (refundOption !== RefundSelectOption.NONE && refundOption !== RefundSelectOption.PARTIAL) {
            setRefundAmount(purchaseBalance < 0 ? purchaseBalance : 0);
        }

        // scenario: Refund Option NONE is selected and we select option full, that time we don't have the refund amount
        // for the purchase, we get it from line item adjustment added in the item
        setInitialRefundAmount(
            refundOption === RefundSelectOption.NONE || refundOption === RefundSelectOption.PARTIAL
                ? -1 * (refundAmount + findPartialRefundAdjustmentAmount(item, breakdown))
                : refundAmount,
        );
    };

    const {
        mutateAsync: updatePurchase,
        isLoading: isUpdating,
        isSuccess,
    } = useMutation(async ({ dryRun = false, ...payload }: UpdatePurchaseArguments) => {
        return api.patch<Purchase>(`/purchases/${purchase.id}`, getOptimizedPayload(payload, initialPurchase), {
            params: { dryRun, validationLevel: validationModalState.validationLevel },
        });
    });

    /**
     * The change has been committed on the server. The modal is on its way out, but it stays on
     * screen while that is handled, so the change must not be submittable again
     */
    const hasCommitted = isSuccess && !isDryRunPurchase;

    const updateGuestsList = (guests: GuestPayload[]) => {
        if (guests.length === 0 || item.id !== defaultItem.id) {
            return guests;
        }

        // This will copy back any removed guests that were removed from the original payload
        const updatedGuests = defaultItem.guests
            .filter((guest) => guest.id)
            .map((initialGuest) => {
                const guest = guests ? guests.find((guest) => guest.id && guest.id === initialGuest.id) : null;
                return guest ? guest : { ...initialGuest, _remove: true };
            });

        // This will add any new guests that were added from the original payload
        const newGuests = guests ? guests.filter((guest) => !guest.id && !guest._remove) : [];
        return [...newGuests, ...updatedGuests];
    };

    const getAttendeesList = (guests: GuestPayload[]): GuestPayload[] => {
        if (localAttendee?.isChangeDemographic) {
            return guests.map(
                (initialGuest) => item.guests.find((guest) => guest.id === initialGuest.id) as GuestPayload,
            );
        }

        return item.guests.map((guest) =>
            guest.id === localAttendee?.removedAttendee ? { ...guest, _remove: true } : guest,
        );
    };

    const updatedGuests = useMemo(
        () => (localAttendee ? getAttendeesList(guests) : updateGuestsList(guests)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [guests],
    );

    // The seller chooses which attendees to drop; the API's getGuestToRemove is only a fallback for
    // requests that carry no guest breakdown. Submission stays blocked until they are under capacity.
    const hasTooManyAttendees = useMemo(() => {
        if (!isEquipmentItem(item) || !experience) {
            return false;
        }
        const maxAttendees = getMaxAttendees(experience, equipmentLineItems);
        return maxAttendees > 0 && updatedGuests.filter((guest) => !guest._remove).length > maxAttendees;
    }, [item, experience, equipmentLineItems, updatedGuests]);

    const isForceRetain =
        !showRetainPriceToggle &&
        (refundOption === RefundSelectOption.PARTIAL || refundOption === RefundSelectOption.NONE);

    const payload = getPayload({
        purchase,
        refundAmount,
        refundOption,
        guests: updatedGuests,
        purchaseItemIds: itemIds,
        retainPrice: isForceRetain ? true : retainPrice,
        showRetainPriceToggle: isForceRetain ? true : showRetainPriceToggle,
        equipmentLineItems: isEquipmentItem(item) ? equipmentLineItems : undefined,
    });

    const handleSubmit = async () => {
        try {
            const isRetainPrice = showRetainPriceToggle && !retainPrice;
            const optimizedPayload = getOptimizedPayload(payload, initialPurchase);
            const hasGuestChanges = optimizedPayload.items.some((item) => item.guests && item.guests.length > 0);
            const hasEquipmentChanges =
                isEquipmentItem(item) && isEquipmentQuantityChanged(item.lineItems, equipmentLineItems);

            if (!hasGuestChanges && !hasEquipmentChanges && !isRetainPrice) {
                return flash.danger("No changes have been made");
            }

            const { data: newPurchase } = await updatePurchase({
                ...optimizedPayload,
                _emailOverrides: { sendEmailToTraveler: notifyCustomer },
            });

            setIsDryRunPurchase(false);

            const updatedItem = getSelectedItem(newPurchase.items, item.id);
            const isChanged = isPurchaseChanged(
                updatedItem,
                defaultItem.guests,
                isEquipmentItem(item) ? (defaultItem.lineItems as LineItem[]) : undefined,
            );
            const hasCustomerEmail = purchase.customerEmail !== "";
            const nextBalance = breakdown?.balance ?? 0;
            const { nextModalOption, nextRefundOption } = getNextPurchaseModificationActions({
                balance: nextBalance,
                hasPurchaseChanges: isChanged,
                hasCustomerEmail,
                canViewInvoice: isGranted(Permission.INVOICE_VIEW),
                refundAmount,
                refundSelection: refundOption,
            });

            if (isChanged) {
                updateCache("purchases", newPurchase, shouldInvalidate);

                if (event && shouldInvalidate && !isDifferentSellerPurchase) {
                    const timeslotParams = {
                        timeslot: null,
                        event: event,
                        productId: null,
                        startDate: null,
                    };

                    const updateParams = {
                        isGranted: isGranted,
                    };

                    await updateTimeslotCache(timeslotParams, updateParams);
                }

                // If you invalidate the cache here the roster will close before the refund modal opens
                // So we wait until refund modal is handled before invalidating the cache
                setTimeout(() => {
                    onClose(nextModalOption, nextRefundOption, nextBalance, newPurchase, item);
                }, 1000);
            } else {
                onClose(nextModalOption, nextRefundOption, 0, undefined, item);
            }

            if (!isChanged || (!nextModalOption && !nextRefundOption)) {
                // Only invalidating here because we know there has been no change, so there will be no refund
                updateCache("purchases", newPurchase);
                if (event && !isDifferentSellerPurchase) {
                    const timeslotParams = {
                        timeslot: null,
                        event: event,
                        productId: null,
                        startDate: null,
                    };

                    const updateParams = {
                        isGranted: isGranted,
                    };

                    await updateTimeslotCache(timeslotParams, updateParams);
                }

                invalidate("events", item.event?.id);
            }

            onContinue?.();
        } catch {
            flash.danger("Error modifying guest quantity");
        }
    };

    const handleGuestsChange = async (guests: GuestPayload[]) => {
        const changedGuests = localAttendee ? guests : updateGuestsList(guests);

        setItem({ ...item, guests: changedGuests as Guest[] });
        const updatedPurchaseItems = purchase.items.map((itemItr) => {
            if (itemItr.id === item.id) {
                return { ...itemItr, guests: changedGuests as Guest[] };
            }

            return itemItr;
        });
        setPurchase({ ...purchase, items: updatedPurchaseItems });
    };

    const handleRefundOptionChange = (amount: number, refundOption?: RefundSelectOption) => {
        setRefundAmount(amount);

        if (refundOption) {
            setRefundOption(refundOption);
        }
    };

    const handleApiValidationSubmit = () => {
        setValidationModalState({ isOpen: false, validationLevel: "info", message: "" });
    };

    const handleApiValidationClose = () => {
        setValidationModalState({ ...validationModalState, isOpen: false, validationLevel: "" });
    };

    const handlePaymentBreakdownError = (error: AxiosError) => {
        const errorData = error.response?.data as QuantityChangeErrorData | undefined;
        const validationErrors = errorData?.errors;
        const errorFromMessageField = findValue(errorData, "message");
        const hasCouponLineItems = getCouponLineItems(item).length > 0;
        const firstValidationError = validationErrors?.[0];
        const errorObject = firstValidationError?.object ?? "";

        if (!validationErrors && errorFromMessageField) {
            flash.danger(errorFromMessageField);
        }

        if (
            hasCouponLineItems &&
            errorObject === PurchaseRuleFilterError.VALIDATION_ERROR &&
            firstValidationError?.code === PurchaseRuleFilterErrorCode.GREATER_THAN_OR_EQUAL_TO_FILTER_NOT_MET
        ) {
            const errorMessage = lowerCase(firstValidationError.message);
            setValidationModalState({
                isOpen: true,
                validationLevel: "",
                message: `In order to use this code, guest ${errorMessage}. Proceeding with this modification will result in the coupon discount to be zeroed out. Do you wish to proceed?`,
            });
        } else if (isEmpty(validationErrors)) {
            throw new Error(String(error));
        } else {
            couponRestrictionModal.show();
        }
    };

    const handleBreakdownUpdate = (response: Purchase | null) => {
        if (response) {
            setIsRetainPriceLoading(false);
            setBreakdown(response); // Set breakdown payload so the amount can be shown for refund options
            if (isPriceConfigurationUpdated(response, item) && !showRetainPriceToggle) {
                setShowRetainPriceToggle(true);
                setRetainPrice(!retainPrice);
                // When PARTIAL/NONE is selected the breakdown already includes _priceStrategy: retain,
                // so flipping the toggle will produce an identical payload — no new probe needed.
                if (!isForceRetain) {
                    setIsRetainPriceLoading(true);
                }
            }

            if (isPriceConfigurationUpdated(response, item) && showRetainPriceToggle) {
                setIsRetainPriceLoading(false);
            }
        }
    };

    const handleChangeItem = (item: PurchaseItem) => {
        const selectedItem = item as ExperiencePurchaseItem;
        setDefaultItem(selectedItem);
        setItem(selectedItem);
        // Without re-seeding, the previous item's equipment quantities get applied to this one.
        setEquipmentLineItems(
            isEquipmentItem(selectedItem)
                ? selectedItem.lineItems?.filter((lineItem) => lineItem.type === LineItemType.EQUIPMENT) ?? []
                : [],
        );
    };

    const breakdownPayload = useMemo(() => {
        return getPayload({
            purchase,
            refundAmount,
            refundOption,
            guests: updatedGuests,
            purchaseItemIds: itemIds,
            retainPrice: isForceRetain ? true : retainPrice,
            showRetainPriceToggle: isForceRetain ? true : showRetainPriceToggle,
            equipmentLineItems: isEquipmentItem(item) ? equipmentLineItems : undefined,
        });
    }, [
        item,
        itemIds,
        updatedGuests,
        purchase,
        retainPrice,
        showRetainPriceToggle,
        refundAmount,
        refundOption,
        isForceRetain,
        equipmentLineItems,
    ]);

    // Omit gifts and store credit since we can't change guest for it.
    const experiencePurchaseItems = getExperiencePurchaseItems(purchase);

    useEffect(() => {
        // uses to avoid demographic list flickering on modal closing
        setTimeout(() => setLocalAttendee(attendee), 300);
    }, [attendee]);

    return (
        <fieldset disabled={isUpdating || hasCommitted} className="space-y-5">
            <Modal.Header>
                {attendee
                    ? "Change Demographic"
                    : isEquipmentItem(item)
                    ? "Change Equipment Quantity"
                    : "Change Guest Quantity"}
            </Modal.Header>

            <Modal.Body className="flex-col">
                {/* Since we are selecting particular guest in a purchase item, we should not show option to switch the purchase item. */}
                {experiencePurchaseItems.length <= 1 ||
                !!localAttendee?.removedAttendee ||
                isSharedDemographicsPackage(pkg) ? null : (
                    <>
                        <Label>Select Reservation</Label>
                        <div className="mb-4 w-full">
                            <PurchaseItemSelect
                                items={experiencePurchaseItems}
                                value={item}
                                className="flex-1"
                                onChange={handleChangeItem}
                            />
                        </div>
                    </>
                )}

                <div className="flex space-x-4">
                    <div className="flex-1 space-y-4">
                        <PurchaseItemSummaryTile purchase={purchase} item={item} />

                        <PurchasePriceStrategy
                            isChecked={retainPrice}
                            isVisible={showRetainPriceToggle}
                            onChange={() => setRetainPrice(!retainPrice)}
                        />
                        {isLoading || !timeslot || isExperienceLoading ? (
                            <Loader />
                        ) : (
                            <DemographicList
                                experience={experience!}
                                timeslot={timeslot}
                                guests={guests as Guest[]}
                                item={item}
                                isLoading={isExperienceLoading}
                                updatedGuests={sortGuestsByDisplayOrder(updatedGuests)}
                                isAttendee={!!localAttendee}
                                package_={pkg}
                                equipmentLineItems={isEquipmentItem(item) ? equipmentLineItems : undefined}
                                onChange={handleGuestsChange}
                                onEquipmentChange={isEquipmentItem(item) ? setEquipmentLineItems : undefined}
                            />
                        )}
                    </div>

                    <div className="flex-1">
                        <div className="min-h-[340px] rounded bg-gray-lighter p-6">
                            {hasLineItems ? (
                                <PaymentBreakdown
                                    isEnabled
                                    payload={breakdownPayload}
                                    purchase={purchase}
                                    item={isPackage ? undefined : defaultItem}
                                    isParentLoading={isRetainPriceLoading}
                                    validationLevel={validationModalState.validationLevel}
                                    isUpdating={setIsPaymentBreakdownLoading}
                                    onUpdate={handleBreakdownUpdate}
                                    onError={handlePaymentBreakdownError}
                                />
                            ) : (
                                <div className="h-72 space-y-2">
                                    <div className="pb-4 text-lg font-bold text-secondary">Purchase Summary</div>
                                    <SummarySkeleton item={item} />
                                </div>
                            )}

                            <div className="mt-4">
                                {isPurchaseChanged(
                                    initialItem,
                                    updatedGuests,
                                    isEquipmentItem(item) ? equipmentLineItems : undefined,
                                ) &&
                                (refundAmount < 0 || !!refundOption) ? (
                                    <RefundOptions
                                        initialAmount={initialRefundAmount}
                                        amount={refundAmount}
                                        onChange={handleRefundOptionChange}
                                    />
                                ) : (
                                    // Dummy div to prevent the modal from jerking up and down
                                    <div className="h-15" />
                                )}
                            </div>
                        </div>
                        {shouldShowPrivacyWarning && (
                            <div className="mt-4 flex w-full flex-col">
                                <Alert color="warning" className="text-left">
                                    This experience no longer supports {item.privacy} pricing. Please change the privacy
                                    for this reservation to {supportedPrivacy} if you would like the latest pricing to
                                    apply.
                                </Alert>
                            </div>
                        )}
                    </div>
                </div>
            </Modal.Body>

            <Modal.Footer>
                <div className="flex justify-end space-x-4">
                    <NotifyCustomerCheckbox
                        isChecked={notifyCustomer}
                        onChange={(e: any) => setNotifyCustomer(e.target.checked)}
                    />

                    {/**
                     * Passing as onClick={onClose} will pass JS event as 1st param which is not expected,
                     * and will cause the JS event to be set as `modalOption`.
                     */}
                    <SecondaryButton onClick={() => onClose(null, null, 0)}>Cancel</SecondaryButton>
                    <SubmitButton
                        isLoading={isUpdating || isPaymentBreakdownLoading}
                        disabled={isUpdating || isPaymentBreakdownLoading || hasCommitted || hasTooManyAttendees}
                        isSuccess={hasCommitted}
                        onClick={handleSubmit}
                    >
                        Modify
                    </SubmitButton>
                </div>
            </Modal.Footer>
            <PromptModal
                title="Coupon Restrictions"
                isOpen={validationModalState.isOpen}
                onClose={handleApiValidationClose}
                onConfirm={handleApiValidationSubmit}
            >
                {validationModalState.message}
            </PromptModal>
            <CouponValidationModal
                isOpen={couponRestrictionModal.isVisible}
                item={item}
                onClose={couponRestrictionModal.hide}
                onSubmit={couponRestrictionModal.hide}
            />
        </fieldset>
    );
};

const getSelectedItem = (purchaseItems: any, selectedItemId: ID) => {
    return purchaseItems.find((item: any) => item.id === selectedItemId);
};

export const useTimeslot = (item: ExperiencePurchaseItem, purchase: Purchase, package_?: Package) => {
    const items = isSharedDemographicsPackage(package_) ? purchase.items : [item];

    // if package find timeslot of all items on that arrival date and use lowest maximum capacity.
    const timeslotQueries = items.map((item) => {
        const experience = getExperienceFromQueryCache(item.product.id);
        const params = getQueryparam(item as ExperiencePurchaseItem, experience);
        return {
            queryKey: ["timeslots", params],
            queryFn: async () => timeslotQueryFunction(params),
            staleTime: 1000,
        };
    });

    const timeslotsResponse = useQueries(timeslotQueries);

    const isLoading = timeslotsResponse.some((response) => response.isLoading);
    if (!isLoading) {
        const allTimeslots = timeslotsResponse.map((response) => response.data);
        const minimumCapacityTimeslot = minBy(allTimeslots, "data.capacity.open") ?? allTimeslots?.[0];
        return { data: minimumCapacityTimeslot?.data, isLoading };
    }

    return { ...timeslotsResponse?.[0], isLoading };
};

const timeslotQueryFunction = async (params: any) => {
    const { data } = await api.get<{ data: Timeslot[] }>("/timeslots", { params });
    const timeslots = Array.isArray(data) ? data : data.data;
    return { data: timeslots?.[0] };
};

const getQueryparam = (item: ExperiencePurchaseItem, experience?: Experience) => {
    const params: any = {
        start: item.arrivalDateTime?.format(),
        product: item.product.id,
        include: ["units", "capacity.max", "event"],
        limit: 1,
    };

    if (isEquipmentItem(item)) {
        params.include.push("units.open");
    }

    if (item.privacy === PurchaseItemPrivacy.PRIVATE || (experience && isAlwaysPrivateExperience(experience))) {
        params.item = item.id;
        params.privacy = PurchaseItemPrivacy.PRIVATE;
    } else if (item.privacy === PurchaseItemPrivacy.PUBLIC) {
        params.privacy = PurchaseItemPrivacy.PUBLIC;
        if (item.event?.id) {
            params.event = item.event.id;
        }
    }

    if (item.arrivalDateTime?.isBefore(now())) {
        params.allowPastSlots = true;
    }

    const isPartnerPackage = isPartnerPackageItem(item);
    const parentProductId = isPackageItem(item) && item?.parentProduct?.id;
    if (parentProductId && !isPartnerPackage) {
        params.package = parentProductId;
    }

    return params;
};

const GUEST_COMPARISON_FIELDS_TO_OMIT = ["checkInCount", "waiver", "ticketCode"] as const;

const getOptimizedPayload = (payload: UpdatePurchasePayload, initialPurchase: Purchase): UpdatePurchasePayload => {
    const optimizedItems = payload.items.map((payloadItem) => {
        const initialItem = initialPurchase.items.find((item) => item.id === payloadItem.id);
        if (!initialItem) {
            return {
                ...payloadItem,
                guests: (payloadItem.guests ?? []).map((guest) => omit(guest, "ticketCode")),
            };
        }

        const initialGuests = initialItem.guests || [];
        const payloadGuests = payloadItem.guests || [];

        const changedGuests = payloadGuests.filter((payloadGuest) => {
            if (payloadGuest._remove) {
                return true;
            }

            if (!payloadGuest.id) {
                return true;
            }

            const initialGuest = initialGuests.find((g) => g.id === payloadGuest.id);
            if (!initialGuest) {
                return true;
            }

            const payloadGuestForComparison = omit(payloadGuest, GUEST_COMPARISON_FIELDS_TO_OMIT);
            const initialGuestForComparison = omit(initialGuest, GUEST_COMPARISON_FIELDS_TO_OMIT);

            return !isEqual(payloadGuestForComparison, initialGuestForComparison);
        });

        const optimizedGuests = changedGuests.map((guest) => {
            if (guest._remove) {
                return { id: guest.id, _remove: true };
            }
            return omit(guest, "ticketCode");
        });

        return {
            ...payloadItem,
            guests: optimizedGuests,
        };
    });

    return { ...payload, items: optimizedItems };
};

interface QuantityChangeErrorData {
    errors?: Array<{ object?: string; code?: string; message: string }>;
    message?: string;
}

interface QuantityChangePayloadParams {
    purchaseItemIds: ID[];
    guests: GuestPayload[];
    purchase: Purchase;
    refundAmount: number;
    refundOption: RefundSelectOption | null;
    equipmentLineItems?: LineItem[];
    retainPrice?: boolean;
    showRetainPriceToggle?: boolean;
}

const getPayload = ({
    purchaseItemIds,
    guests,
    purchase,
    refundAmount,
    refundOption,
    equipmentLineItems,
    retainPrice,
    showRetainPriceToggle,
}: QuantityChangePayloadParams): UpdatePurchasePayload => {
    const items = purchase.items
        .filter((purchaseItem) => purchaseItemIds.includes(purchaseItem.id))
        .map((item) => {
            const isEquipment = isEquipmentItem(item);
            return {
                id: item.id,
                guests: guests.map((guest) => omit(guest, ["checkInCount", "ticketCode", "waiver"])) as Guest[],
                ...(isEquipment && equipmentLineItems
                    ? {
                          lineItems: equipmentLineItems.map((li) => ({
                              type: li.type,
                              quantity: li.quantity,
                              template: li.template,
                              name: li.name,
                              includeInAmount: li.includeInAmount,
                          })),
                      }
                    : null),
                ...(showRetainPriceToggle && retainPrice ? { _priceStrategy: PriceStrategy.RETAIN } : null),
                ...(refundOption === RefundSelectOption.PARTIAL || refundOption === RefundSelectOption.NONE
                    ? { _partialRefund: refundAmount }
                    : null),
            };
        });

    return { items };
};

const getEquipmentQuantities = (lineItems?: LineItem[]) =>
    groupBy(
        lineItems?.filter((li) => li.type === LineItemType.EQUIPMENT),
        "template.id",
    );

const isEquipmentQuantityChanged = (initialLineItems?: LineItem[], updatedLineItems?: LineItem[]) => {
    const initialQuantities = getEquipmentQuantities(initialLineItems);
    const updatedQuantities = getEquipmentQuantities(updatedLineItems);
    const templateIds = new Set([...Object.keys(initialQuantities), ...Object.keys(updatedQuantities)]);

    for (const templateId of templateIds) {
        const initialQuantity = initialQuantities[templateId]?.[0]?.quantity ?? 0;
        const updatedQuantity = updatedQuantities[templateId]?.[0]?.quantity ?? 0;
        if (initialQuantity !== updatedQuantity) {
            return true;
        }
    }

    return false;
};

const isPurchaseChanged = (
    initialItem: PurchaseItem,
    updatedGuests: GuestPayload[],
    updatedEquipmentLineItems?: LineItem[],
) => {
    const initialGuests = groupBy(initialItem.guests, "unit.id");
    const newGuests = groupBy(
        updatedGuests.filter((guest) => !guest._remove),
        "unit.id",
    );

    for (const key of Object.getOwnPropertyNames(initialGuests)) {
        if (!newGuests[key] || newGuests[key].length !== initialGuests[key].length) {
            return true;
        }
    }

    if (updatedEquipmentLineItems && isEquipmentQuantityChanged(initialItem.lineItems, updatedEquipmentLineItems)) {
        return true;
    }

    return false;
};

const findPartialRefundAdjustmentAmount = (item: PurchaseItem, purchase?: Purchase | null): number => {
    const purchaseItem = purchase?.items.find(({ id }) => id === item.id);

    const lineItem = findLast(
        purchaseItem?.lineItems,
        ({ name, type }) => type === LineItemType.TYPE_ADHOC_ADJUSTMENT && name === "Reduce guests (partial refund)",
    );

    return lineItem?.amount ?? 0;
};

const getGuestsWithoutCheckInCount = (guests: Guest[]): GuestPayload[] => {
    return guests.map((guest) => omit(guest, "checkInCount"));
};
